<script lang="ts">
  import FilesPanel from '../../FilesPanel.svelte';
  import { Plus } from 'lucide-svelte';
  import { selectEntity } from '$lib/router';
  import { roomsQuery } from '$lib/queries/rooms';
  import { loadsQuery } from '$lib/queries/loads';
  import { zonesQuery } from '$lib/queries/zones';
  import { inputDevicesQuery } from '$lib/queries/input_devices';
  import { scenesQuery } from '$lib/queries/scenes';
  import { unitSceneLoadsQuery } from '$lib/queries/scene_loads';
  import { createSceneMutation } from '$lib/mutations/scenes';
  import { router } from '$lib/router';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api';
  import { LOAD_TYPES } from '$lib/constants/load';
  import type { SceneResponse } from '$lib/types/scene';
  import type { InputDeviceResponse } from '$lib/types/input_device';

  let { id }: { id: number } = $props();

  const unitId = $derived(router.unitId ?? 0);
  const unitRooms = roomsQuery();
  const unitLoads = loadsQuery();
  const unitZones = zonesQuery();
  const unitInputDevices = inputDevicesQuery();
  const unitScenes = scenesQuery();
  const createScene = createSceneMutation();

  const room = $derived((unitRooms.data ?? []).find((r) => r.id === id));
  const zone = $derived(room ? (unitZones.data ?? []).find((z) => z.id === room.zone) : null);
  const roomLoads = $derived((unitLoads.data ?? []).filter((l) => l.room === id));
  const roomKeypads = $derived((unitInputDevices.data ?? []).filter((d) => d.location_room === id));
  const roomLoadIds = $derived(new Set(roomLoads.map((l) => l.id)));

  const unitSceneLoads = unitSceneLoadsQuery();

  const roomScenes = $derived(
    (unitScenes.data ?? []).filter((scene: SceneResponse) => {
      if (scene.unit !== unitId) return false;
      const slForScene = (unitSceneLoads.data ?? []).filter((sl) => sl.scene === scene.id);
      return slForScene.some((sl) => roomLoadIds.has(sl.load_id));
    })
  );

  function sceneLoadCount(sceneId: number) {
    return (unitSceneLoads.data ?? []).filter(
      (sl) => sl.scene === sceneId && roomLoadIds.has(sl.load_id)
    ).length;
  }

  function addLoad() {
    document.dispatchEvent(new CustomEvent('open-create-load', { detail: { roomId: id } }));
  }

  function addDevice() {
    document.dispatchEvent(new CustomEvent('open-create-device', { detail: { roomId: id } }));
  }

  function addScene() {
    const name = prompt('Scene name');
    if (!name?.trim() || !unitId) return;
    createScene.mutate(
      {
        unit: unitId,
        name: name.trim(),
        scope: 'room',
        scope_id: id,
        priority: 1
      },
      {
        onSuccess: (newScene) => {
          toast.success(`Scene "${newScene.name}" created`);
          selectEntity('scene', newScene.id);
        },
        onError: (error) => toast.error(getErrorMessage(error))
      }
    );
  }
</script>

<div class="p-3 space-y-4">
  <div>
    <h2 class="text-base font-medium text-text-primary">{room?.name ?? `Room ${id}`}</h2>
    {#if zone}
      <p class="text-xs text-text-muted">{zone.name}</p>
    {/if}
  </div>

  <section>
    <div class="flex items-center justify-between mb-2">
      <h3 class="text-sm font-medium text-text-primary">Loads</h3>
      <button
        type="button"
        class="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary"
        onclick={addLoad}
      >
        <Plus class="w-3.5 h-3.5" />
        Add load
      </button>
    </div>

    {#if roomLoads.length === 0}
      <p class="text-sm text-text-muted py-2">No loads yet.</p>
    {:else}
      <div class="border border-border-default rounded overflow-hidden">
        <table class="w-full text-sm">
          <thead class="bg-bg-rail text-text-muted text-xs">
            <tr class="h-8">
              <th class="text-left px-3 font-medium">Name</th>
              <th class="text-left px-3 font-medium">Type</th>
              <th class="text-left px-3 font-medium w-20">Qty</th>
              <th class="text-right px-3 font-medium w-20">W</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-border-default">
            {#each roomLoads as load (load.id)}
              <tr
                class="h-8 hover:bg-bg-hover cursor-pointer"
                onclick={() => selectEntity('load', load.id)}
              >
                <td class="px-3 font-medium">{load.name}</td>
                <td class="px-3 text-text-secondary">
                  {LOAD_TYPES[load.load_type as keyof typeof LOAD_TYPES]?.label ?? load.load_type}
                </td>
                <td class="px-3 font-mono text-text-secondary">{load.quantity ?? 1}</td>
                <td class="px-3 text-right font-mono text-text-muted">
                  {load.total_wattage ?? load.wattage_per_unit ?? '—'}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </section>

  <section>
    <div class="flex items-center justify-between mb-2">
      <h3 class="text-sm font-medium text-text-primary">Devices</h3>
      <button
        type="button"
        class="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary"
        onclick={addDevice}
      >
        <Plus class="w-3.5 h-3.5" />
        Add device
      </button>
    </div>

    {#if roomKeypads.length === 0}
      <p class="text-sm text-text-muted py-2">No keypads in this room.</p>
    {:else}
      <div class="border border-border-default rounded overflow-hidden">
        <table class="text-sm w-full max-w-xs">
          <thead class="bg-bg-rail text-text-muted text-xs">
            <tr class="h-8">
              <th class="text-left px-3 font-medium">Name</th>
              <th class="text-right px-3 font-medium w-20">Keys</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-border-default">
            {#each roomKeypads as keypad (keypad.id)}
              <tr
                class="h-8 hover:bg-bg-hover cursor-pointer"
                onclick={() => selectEntity('keypad', keypad.id)}
              >
                <td class="px-3 font-medium">{keypad.name || `Keypad ${keypad.id}`}</td>
                <td class="px-3 text-right font-mono">{keypad.keys?.length ?? 0}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </section>

  <section>
    <div class="flex items-center justify-between mb-2">
      <h3 class="text-sm font-medium text-text-primary">Scenes touching room</h3>
      <button
        type="button"
        class="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary"
        onclick={addScene}
      >
        <Plus class="w-3.5 h-3.5" />
        Add scene
      </button>
    </div>

    {#if roomScenes.length === 0}
      <p class="text-sm text-text-muted py-2">No scenes include loads from this room.</p>
    {:else}
      <div class="border border-border-default rounded overflow-hidden">
        <table class="w-full text-sm">
          <thead class="bg-bg-rail text-text-muted text-xs">
            <tr class="h-8">
              <th class="text-left px-3 font-medium">Scene</th>
              <th class="text-left px-3 font-medium">Scope</th>
              <th class="text-right px-3 font-medium w-24">Loads</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-border-default">
            {#each roomScenes as scene (scene.id)}
              <tr
                class="h-8 hover:bg-bg-hover cursor-pointer"
                onclick={() => selectEntity('scene', scene.id)}
              >
                <td class="px-3 font-medium">{scene.name}</td>
                <td class="px-3 text-text-secondary capitalize">{scene.scope ?? '—'}</td>
                <td class="px-3 text-right font-mono">{sceneLoadCount(scene.id)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </section>
  {#if room && router.projectId && unitId}
    <section class="border-t border-border-default pt-4">
      <h3 class="text-sm font-medium text-text-primary mb-2">Room files</h3>
      {#key id}
        <FilesPanel projectId={router.projectId} {unitId} roomId={id} emptyLabel="No files uploaded for this room" />
      {/key}
    </section>
  {/if}
</div>
